import { Close } from '@mui/icons-material';
import { Button, IconButton, Radio, Stack, type SxProps, Tooltip, Typography } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import {
    useAutomaticCloseCropMonitoringAlertConditions,
    useCornCropMonitoringAlertConditions,
    useGeneralCropMonitoringAlertConditions,
} from '/@/core/crop-monitoring';
import settings from '/@/core/settings';
import { useMutateArchiveCropMonitoringById } from '/@/hooks/crop-monitoring/use-crop-monitoring';
import { markNotificationTrackerAsRead } from '/@/hooks/notification-tracker';
import { useHasWriteAccess } from '/@/hooks/session';
import { useToggle } from '/@/hooks/toggle/useToggle';
import { _ } from '/@/locale';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import type { Field } from '/@/types/field';
import { reverse } from '/@/utils/router';
import ModalV2 from '../../bayer/modal-v2';
import Icon from '../../icon';
import Markdown from '../../markdown';
import colors from '../../theme-provider/colors';

const useCropMonitoringAlert = ({
    cropMonitoring,
    field,
    setOpenFinalizeCampaignModal,
    setOpenHarvestModal,
}: {
    cropMonitoring?: CropMonitoring;
    field: Field;
    setOpenFinalizeCampaignModal: () => void;
    setOpenHarvestModal: () => void;
}) => {
    const cornAlertConditions = useCornCropMonitoringAlertConditions({ cropMonitoring });
    const generalAlertConditions = useGeneralCropMonitoringAlertConditions({ cropMonitoring });
    const notificationTrackersConditions = useAutomaticCloseCropMonitoringAlertConditions({ field });

    return useMemo(() => {
        let alertToShow: string | undefined;
        if (cropMonitoring?.crop === 'corn') {
            if (cornAlertConditions.showDoYouHarvestYourFieldAlert) {
                alertToShow = 'showDoYouHarvestYourFieldAlert';
            }
            if (cornAlertConditions?.showHowDidYourHarvestGoAlert) {
                alertToShow = 'showHowDidYourHarvestGoAlert';
            }
        } else {
            if (generalAlertConditions.showDoYouHarvestYourFieldAlert) {
                alertToShow = 'showDoYouHarvestYourFieldAlert';
            }
        }

        if (notificationTrackersConditions.notificationTrackersAlert) {
            alertToShow = 'showAutomaticCloseAlert';
        }

        if (!alertToShow) {
            return undefined;
        }

        return {
            showDoYouHarvestYourFieldAlert: {
                color: colors.themes[settings.app.theme].mui.primary.infoBackground,
                textColor: 'primary',
                title: _('¿Ya cosechaste este lote?'),
                description: _(
                    'Cargá los datos de cosecha para obtener un reporte detallado. Podés importarlos automáticamente desde Fieldview o cargarlos a mano.'
                ),
                buttonText: _('Obtener reporte'),
                buttonDisabled: false,
                secondaryBtnText: _('Finalizar campaña sin cargar datos'),
                onClickSecondaryBtn: () => {
                    setOpenFinalizeCampaignModal();
                },
                onClickBtn: () => {
                    setOpenHarvestModal();
                },
            },
            showHowDidYourHarvestGoAlert: {
                color: colors.themes?.[settings.app.theme]?.warning[50],
                textColor: colors.themes?.[settings.app.theme]?.warning[600],
                title: _('¿Como te fue en la cosecha?'),
                description: _(
                    'Ya pasaron algunos días desde la fecha estimada y queremos ayudarte a analizar los resultados. Cargá los datos para obtener un reporte detallado.'
                ),
                buttonText: _('Obtener reporte'),
                buttonDisabled: false,
                secondaryBtnText: _('Finalizar campaña sin cargar datos'),
                onClickSecondaryBtn: () => {
                    setOpenFinalizeCampaignModal();
                },
                onClickBtn: () => {
                    setOpenHarvestModal();
                },
            },
            showAutomaticCloseAlert: {
                color: colors.themes[settings.app.theme].mui.primary.infoBackground,
                textColor: 'primary',
                title: _('Se inició una nueva campaña'),
                description: _(
                    'Guardamos los datos de la campaña anterior en reportes de campaña. Si querés, podés cargar los datos de cosecha y obtener el reporte para analizarlos cuando lo necesites.'
                ),
                buttonText: _('Obtener reporte'),
                buttonDisabled: false,
                onClickBtn: () => {
                    setOpenHarvestModal();
                    if (notificationTrackersConditions.notificationTrackersAlert)
                        markNotificationTrackerAsRead(notificationTrackersConditions.notificationTrackersAlert);
                },
                onClickCloseAlert: () => {
                    if (notificationTrackersConditions.notificationTrackersAlert)
                        markNotificationTrackerAsRead(notificationTrackersConditions.notificationTrackersAlert);
                },
            },
        }[alertToShow];
    }, [
        cropMonitoring,
        cornAlertConditions,
        generalAlertConditions,
        notificationTrackersConditions.notificationTrackersAlert,
        setOpenFinalizeCampaignModal,
        setOpenHarvestModal,
    ]);
};

const HarvestAlert = ({
    cropMonitoring,
    field,
    setOpenFinalizeCampaignModal,
    setOpenHarvestModal,
}: {
    cropMonitoring?: CropMonitoring;
    field: Field;
    setOpenFinalizeCampaignModal: () => void;
    setOpenHarvestModal: () => void;
}) => {
    const alert = useCropMonitoringAlert({ cropMonitoring, field, setOpenFinalizeCampaignModal, setOpenHarvestModal });

    const alertIsDisabled = !useHasWriteAccess();

    if (!alert || alertIsDisabled) {
        return null;
    }

    const {
        color,
        textColor,
        title,
        description,
        secondaryBtnText,
        onClickSecondaryBtn,
        buttonText,
        onClickBtn,
        buttonDisabled,
        onClickCloseAlert,
    } = alert;

    return (
        <Stack direction='column' spacing={1} sx={{ background: color, p: 2, mb: 4, position: 'relative' }}>
            <Typography variant='subtitle1' fontWeight={600} color={textColor}>
                {title}
            </Typography>
            <Typography variant='subtitle2' color={textColor}>
                {description}
            </Typography>
            <Stack direction='row' spacing={1} justifyContent='end'>
                {secondaryBtnText && (
                    <Button
                        variant='text'
                        color='primary'
                        onClick={onClickSecondaryBtn}
                        sx={{ textDecoration: 'underline' }}
                    >
                        {secondaryBtnText}
                    </Button>
                )}
                {buttonText && (
                    <Button
                        variant='contained'
                        color='primary'
                        sx={{ width: 'fit-content' }}
                        onClick={onClickBtn}
                        disabled={buttonDisabled}
                    >
                        {buttonText}
                    </Button>
                )}
            </Stack>
            {onClickCloseAlert && (
                <IconButton
                    onClick={onClickCloseAlert}
                    sx={{ position: 'absolute', top: 0, right: 0, margin: '8px !important' }}
                    size='small'
                >
                    <Close color='primary' />
                </IconButton>
            )}
        </Stack>
    );
};

function SimpleCardList({
    title,
    description,
    onClick,
    isSelected,
    disabled,
    tooltip,
    sx,
}: {
    title: string;
    description: string | React.ReactNode;
    onClick: () => void;
    isSelected: boolean;
    disabled?: boolean;
    tooltip?: string;
    sx?: SxProps;
}) {
    return (
        <Tooltip title={tooltip || ''}>
            <Stack
                border={`1px solid ${isSelected ? colors.themes[settings.app.theme].mui.secondary.secondary : 'lightgray'}`}
                alignItems='center'
                backgroundColor={
                    isSelected ? colors.themes[settings.app.theme].mui.primary.itemSelectedBackground : undefined
                }
                onClick={disabled ? undefined : onClick}
                borderRadius='4px'
                sx={{
                    cursor: disabled ? 'unset' : 'pointer',
                    p: 2,
                    boxSizing: 'border-box',
                    opacity: disabled ? 0.5 : 1,
                    ...sx,
                }}
            >
                <Stack direction='row' justifyContent='space-between' alignItems='start' width='100%'>
                    <Typography variant='h6' color={'primary'}>
                        {title}
                    </Typography>
                    <Radio size='medium' checked={isSelected} color='secondary' />
                </Stack>
                {description && (
                    <Typography variant='body2' color={'primary'} textAlign='left' width='100%'>
                        {description}
                    </Typography>
                )}
            </Stack>
        </Tooltip>
    );
}

export const FinalizeCampaignModal = ({
    cropMonitoring,
    open,
    onClose,
    setOpenHarvestModal,
    setCloseHarvestModal,
}: {
    cropMonitoring: CropMonitoring;
    open: boolean;
    onClose: () => void;
    //
    setOpenHarvestModal: () => void;
    setCloseHarvestModal: () => void;
}) => {
    const navigate = useNavigate();
    const archiveCropMonitoringMutation = useMutateArchiveCropMonitoringById(cropMonitoring?.id);

    return (
        <ModalV2
            title={_('¿Querés finalizar la campaña sin cargar los datos de la cosecha?')}
            open={open}
            onClose={onClose}
            actions={
                <>
                    <Button
                        variant='text'
                        color='secondary'
                        onClick={() => {
                            onClose();
                            setOpenHarvestModal();
                        }}
                    >
                        {_('Cancelar')}
                    </Button>
                    <Button
                        variant='contained'
                        color='secondary'
                        disabled={archiveCropMonitoringMutation.isMutating}
                        onClick={async () => {
                            await archiveCropMonitoringMutation.trigger();
                            if (cropMonitoring?.field)
                                navigate(
                                    reverse('fields.FieldDetail', {
                                        params: {
                                            fieldId: cropMonitoring.field,
                                        },
                                    })
                                );
                            setCloseHarvestModal();
                            onClose();
                        }}
                    >
                        {_('Finalizar sin datos')}
                    </Button>
                </>
            }
            actionsProps={{ sx: { justifyContent: 'end' } }}
            bodyProps={{ sx: { overflow: 'auto', maxHeight: { xs: '80vh' }, textAlign: 'left' } }}
        >
            {_(
                'Si confirmas, no se generará un reporte de esta campaña. Ese reporte puede ayudarte a tomar mejores decisiones en la próxima campaña.'
            )}
        </ModalV2>
    );
};

const HarvestModal = ({
    field,
    cropMonitoring,
    open,
    setClose,
    //
    setCreateReportModalIsOpen,
    setCloseHarvestModal,
    setOpenFinalizeCampaignModal,
    //
    selectedOption,
    setSelectedOption,
}: {
    field: Field;
    cropMonitoring?: CropMonitoring;
    open: boolean;
    setClose: () => void;
    //
    setCreateReportModalIsOpen: (bool: boolean) => void;
    setCloseHarvestModal: () => void;
    setOpenFinalizeCampaignModal: () => void;
    //
    selectedOption?: string;
    setSelectedOption: (option: string) => void;
}) => {
    const options = [
        {
            id: 'fieldview',
            title: _('Importar automáticamente desde FieldView'),
            description: (
                <Stack direction='column' spacing={1}>
                    <Typography color='primary' variant='subtitle2'>
                        {_('Obtené un reporte completo con:')}
                        <ul style={{ paddingLeft: 20 }}>
                            <li>{_('Humedad al momento de cosecha')}</li>
                            <li>{_('Historial de precipitaciones y heladas')}</li>
                            <li>{_('Aplicaciones de fitosanitarios y fertilización')}</li>
                            <li>{_('Informe de cosecha')}</li>
                        </ul>
                    </Typography>
                </Stack>
            ),
            disabled: field?.externalProvider !== 'fieldview',
            tooltip: field?.externalProvider !== 'fieldview' ? _('Este lote no está vinculado a uno en FieldView') : '',
        },
        {
            id: 'manual',
            title: _('Cargar datos manualmente'),
            description: (
                <Stack direction='column' spacing={1}>
                    <Typography color='primary' variant='subtitle2'>
                        {_('Obtené un reporte simple con:')}
                        <ul style={{ paddingLeft: 20 }}>
                            <li>{_('Humedad al momento de cosecha')}</li>
                            <li>{_('Historial de precipitaciones y heladas')}</li>
                        </ul>
                    </Typography>
                </Stack>
            ),
        },
    ];

    return (
        <ModalV2
            open={open}
            onClose={setClose}
            title={_('¿Ya cosechaste este lote?')}
            maxWidth='md'
            bodyProps={{ alignItems: 'stretch', sx: { textAlign: 'left' } }}
            actionsProps={{ sx: { justifyContent: 'flex-end' } }}
            actions={
                <>
                    {cropMonitoring && cropMonitoring.cornCropMonitoring?.phenology !== 'harvested' && (
                        <Button
                            variant='text'
                            color='secondary'
                            onClick={() => {
                                setCloseHarvestModal();
                                setOpenFinalizeCampaignModal();
                            }}
                        >
                            {_('Finalizar campaña sin cargar datos')}
                        </Button>
                    )}
                    <Button
                        variant='contained'
                        color='secondary'
                        onClick={() => {
                            setCreateReportModalIsOpen(true);
                        }}
                    >
                        {_('Continuar')}
                    </Button>
                </>
            }
        >
            <Stack sx={{ px: 2 }} spacing={2} direction='column'>
                <Typography color='primary.mui.inactive' variant='body2'>
                    {_('Elegí cómo cargar los datos de cosecha para obtener un reporte detallado.')}
                </Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                    {options.map((option) => {
                        return (
                            <SimpleCardList
                                key={option.id}
                                title={option.title}
                                description={option.description}
                                isSelected={selectedOption === option.id}
                                onClick={() => setSelectedOption(option.id)}
                                sx={{ minWidth: { xs: '100%', md: 350 }, width: '50%' }}
                                disabled={option?.disabled}
                                tooltip={option?.tooltip}
                            />
                        );
                    })}
                </Stack>
            </Stack>
        </ModalV2>
    );
};

const AutomaticClosedCMModal = ({
    open,
    onClose,
    setOpenHarvestModal,
}: {
    open: boolean;
    onClose: () => void;
    setOpenHarvestModal: () => void;
}) => {
    return (
        <ModalV2
            open={open}
            onClose={onClose}
            title={_('Iniciamos una nueva campaña en este lote')}
            actions={
                <>
                    <Button variant='text' color='secondary' onClick={onClose}>
                        {_('Cerrar')}
                    </Button>
                    <Button
                        variant='contained'
                        color='secondary'
                        onClick={() => {
                            onClose();
                            setOpenHarvestModal();
                        }}
                        startIcon={<Icon type='agronomy' size='xxs' sx={{ fill: 'white' }} />}
                    >
                        {_('Agregar cultivo')}
                    </Button>
                </>
            }
            actionsProps={{
                sx: {
                    justifyContent: 'flex-end',
                },
            }}
            maxWidth='md'
        >
            <Stack spacing={2}>
                <Typography color='primary' variant='body1'>
                    <Markdown>
                        {_(
                            'Pasó un tiempo considerable desde la fecha probable de cosecha, por eso guardamos los datos en <strong>reportes de campaña</strong> y damos inicio a una nueva campaña.'
                        )}
                    </Markdown>
                </Typography>
                <Typography color='primary' variant='body1'>
                    {_('Ahora podés registrar el próximo cultivo para empezar un nuevo seguimiento.')}
                </Typography>
            </Stack>
        </ModalV2>
    );
};

const HarvestLabelAndModal = ({
    field,
    cropMonitoring,
    //
    createReportModalIsOpen,
    setCreateReportModalIsOpen,
    //
    selectedOption,
    setSelectedOption,
    //
    showErrorModal,
    //
    showAlert,
    automaticOpenModals,
}: {
    field: Field;
    cropMonitoring?: CropMonitoring;
    //
    createReportModalIsOpen: boolean;
    setCreateReportModalIsOpen: (bool: boolean) => void;
    //
    selectedOption?: string;
    setSelectedOption: (option: string) => void;
    //
    showErrorModal: boolean;
    //
    showAlert: boolean;
    automaticOpenModals: boolean;
}) => {
    const {
        open: openHarvestModal,
        setOpen: setOpenHarvestModal,
        setClose: setCloseHarvestModalBase,
    } = useToggle(false);
    const {
        open: openFinalizeCampaignModal,
        setOpen: setOpenFinalizeCampaignModal,
        setClose: setCloseFinalizeCampaignModal,
    } = useToggle(false);
    const {
        open: openAutomaticClosedCMModal,
        setOpen: setOpenAutomaticClosedCMModal,
        setClose: setCloseAutomaticClosedCMModal,
    } = useToggle(false);

    const setCloseHarvestModal = () => {
        localStorage.setItem(`harvestModalClosed-${cropMonitoring?.id}`, 'true');
        setCloseHarvestModalBase();
    };

    const canEditResource = useHasWriteAccess();

    const cornAlertConditions = useCornCropMonitoringAlertConditions({ cropMonitoring });
    const generalAlertConditions = useGeneralCropMonitoringAlertConditions({ cropMonitoring });
    const notificationTrackersConditions = useAutomaticCloseCropMonitoringAlertConditions({ field });

    console.log('HarvestLabelAndModal', {
        cornAlertConditions,
        generalAlertConditions,
        notificationTrackersConditions,
        field,
        cropMonitoring,
    });

    useEffect(() => {
        if (!automaticOpenModals) return;

        // Automatic Closed CM Modal
        if (notificationTrackersConditions.notificationTrackersModal) {
            return setOpenAutomaticClosedCMModal();
        }

        // Harvest Modal
        if (!cropMonitoring) return;
        let shouldOpenModal = false;
        if (cropMonitoring.crop === 'corn') {
            shouldOpenModal = cornAlertConditions.showDoYouHarvestYourFieldAlert;
        } else {
            shouldOpenModal = generalAlertConditions.showDoYouHarvestYourFieldAlert;
        }
        const harvestModalClosed = localStorage.getItem(`harvestModalClosed-${cropMonitoring.id}`) === 'true';
        if (shouldOpenModal && !harvestModalClosed) {
            setOpenHarvestModal();
        }
    }, [
        cropMonitoring,
        cornAlertConditions,
        generalAlertConditions,
        notificationTrackersConditions.notificationTrackersModal,
        automaticOpenModals,
    ]);

    if (!canEditResource) {
        return null;
    }

    return (
        <>
            {showAlert && (
                <HarvestAlert
                    cropMonitoring={cropMonitoring}
                    field={field}
                    setOpenFinalizeCampaignModal={setOpenFinalizeCampaignModal}
                    setOpenHarvestModal={setOpenHarvestModal}
                />
            )}

            <HarvestModal
                field={field}
                cropMonitoring={cropMonitoring}
                open={openHarvestModal && !createReportModalIsOpen && !showErrorModal}
                setClose={setCloseHarvestModal}
                //
                setCreateReportModalIsOpen={setCreateReportModalIsOpen}
                setCloseHarvestModal={setCloseHarvestModal}
                setOpenFinalizeCampaignModal={setOpenFinalizeCampaignModal}
                //
                selectedOption={selectedOption}
                setSelectedOption={setSelectedOption}
            />

            {cropMonitoring && (
                <FinalizeCampaignModal
                    cropMonitoring={cropMonitoring}
                    open={openFinalizeCampaignModal}
                    onClose={setCloseFinalizeCampaignModal}
                    //
                    setOpenHarvestModal={setOpenHarvestModal}
                    setCloseHarvestModal={setCloseHarvestModal}
                />
            )}

            <AutomaticClosedCMModal
                open={openAutomaticClosedCMModal}
                setOpenHarvestModal={setOpenHarvestModal}
                onClose={() => {
                    setCloseAutomaticClosedCMModal();
                    if (notificationTrackersConditions.notificationTrackersModal)
                        markNotificationTrackerAsRead(notificationTrackersConditions.notificationTrackersModal);
                }}
            />
        </>
    );
};

export default HarvestLabelAndModal;
